/**
 * Community UTM details helpers (sessionStorage only).
 * Used by GoKollab + ClientPortal for traffic page-view + future signup wiring.
 */

import { useLogger } from '@common/core-layer/composables/useLogger'

import { COMMUNITY_UTM_SESSION_KEY, UTM_QUERY_KEYS } from '../constants/utm-session.constants'
import type { CommunityUtmDetails, UtmQueryInput, UtmQueryKey } from '../types/utm-session.types'

export { COMMUNITY_UTM_SESSION_KEY, UTM_QUERY_KEYS } from '../constants/utm-session.constants'
export type { CommunityUtmDetails, UtmQueryInput, UtmQueryKey } from '../types/utm-session.types'

type QueryValue = string | string[] | null | undefined

function firstQueryString(value: QueryValue): string | undefined {
  if (value == null) return undefined
  const raw = Array.isArray(value) ? value[0] : value
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function canUseSessionStorage(): boolean {
  try {
    return typeof sessionStorage !== 'undefined'
  } catch {
    return false
  }
}

function readDocumentReferrer(): string | undefined {
  try {
    if (typeof document === 'undefined') return undefined
    const referrer = document.referrer?.trim()
    return referrer && referrer.length > 0 ? referrer : undefined
  } catch {
    return undefined
  }
}

/** Normalize untrusted session JSON into allow-listed string fields only. */
function normalizeCommunityUtmDetails(value: Record<string, unknown>): CommunityUtmDetails | null {
  const utm: CommunityUtmDetails = {}
  let hasAny = false

  for (const key of UTM_QUERY_KEYS) {
    const field = value[key]
    if (typeof field === 'string' && field.trim()) {
      utm[key as UtmQueryKey] = field.trim()
      hasAny = true
    }
  }

  if (typeof value.utmReferrer === 'string' && value.utmReferrer.trim()) {
    utm.utmReferrer = value.utmReferrer.trim()
    hasAny = true
  }

  if (typeof value.capturedAt === 'string' && value.capturedAt.trim()) {
    utm.capturedAt = value.capturedAt.trim()
  }

  return hasAny ? utm : null
}

/** Parse allow-listed utm_* from a route/location query. Returns null when none present. */
export function parseUtmFromQuery(query: UtmQueryInput | undefined | null): CommunityUtmDetails | null {
  if (!query) return null

  const parsed: CommunityUtmDetails = {}
  let hasAny = false

  for (const key of UTM_QUERY_KEYS) {
    const value = firstQueryString(query[key])
    if (value) {
      parsed[key] = value
      hasAny = true
    }
  }

  return hasAny ? parsed : null
}

export function readCommunityUtmDetails(): CommunityUtmDetails | null {
  const { logger } = useLogger()

  if (!canUseSessionStorage()) {
    return null
  }

  try {
    const raw = sessionStorage.getItem(COMMUNITY_UTM_SESSION_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      logger.warn('[CommunityUtm] Ignoring invalid session value', {
        labels: { key: COMMUNITY_UTM_SESSION_KEY },
      })
      return null
    }
    return normalizeCommunityUtmDetails(parsed as Record<string, unknown>)
  } catch (error) {
    logger.warn('[CommunityUtm] Failed to read session UTM', {
      error: error instanceof Error ? error : new Error(String(error)),
    })
    return null
  }
}

/** Replace stored UTM details. */
export function writeCommunityUtmDetails(utm: CommunityUtmDetails): void {
  const { logger } = useLogger()

  if (!canUseSessionStorage()) {
    return
  }

  const next: CommunityUtmDetails = { ...utm, capturedAt: new Date().toISOString() }
  try {
    sessionStorage.setItem(COMMUNITY_UTM_SESSION_KEY, JSON.stringify(next))
  } catch (error) {
    logger.warn('[CommunityUtm] Failed to write session UTM', {
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }
}

/**
 * If query contains UTMs, overwrite session UTM details.
 * Also stores document.referrer as utmReferrer when available.
 * If query has no UTMs, leave existing session value unchanged.
 * @returns true when session was updated
 */
export function captureCommunityUtmFromQuery(query: UtmQueryInput | undefined | null): boolean {
  const parsed = parseUtmFromQuery(query)
  if (!parsed) return false

  const referrer = readDocumentReferrer()
  if (referrer) {
    parsed.utmReferrer = referrer
  }

  writeCommunityUtmDetails(parsed)
  return true
}
